import type { Context, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext } from 'react';

import {
  useGetGlobalApiAccountAccess,
  usePatchGlobalApiAccountAccess,
  usePostGlobalApiAccountAccessCancel,
} from '@data/account-access';

import type { PatchScaSubjectBody } from '../../providers/ScaSubjectProvider/ScaSubjectProvider.types';

import { SCA_ACCOUNT_ACCESS_CONTEXT_DEFAULT } from './ScaAccountAccessContext.shared-mocks';
import type {
  ScaAccountAccessContextData,
  ScaAccountAccessContextProviderProps,
} from './ScaAccountAccessContext.types';

const ScaAccountAccessContext =
  createContext<ScaAccountAccessContextData | null>(null);

export const useScaAccountAccessContext = () => {
  const context = useContext(ScaAccountAccessContext);

  if (!context) {
    throw new Error(
      'useScaAccountAccessContext hook must be used within a <ScaAccountAccessContextProvider>'
    );
  }

  return context;
};

export const ScaAccountAccessContextProvider = ({
  enabled,
  authToken,
  accountAccessId,
  children,
}: ScaAccountAccessContextProviderProps) => {
  const {
    error: getError,
    isError: isGetError,
    isLoading: isGetLoading,
    data: accountAccess,
    refetch: retryGet,
  } = useGetGlobalApiAccountAccess({
    accountAccessId,
    authToken,
    enabled,
    voltApp: 'scaApp',
  });

  const {
    mutateAsync: patchAccountAccess,
    isError: isPatchError,
    error: patchError,
    isPending: isPatchFetching,
  } = usePatchGlobalApiAccountAccess({
    accountAccessId,
    authToken,
    voltApp: 'scaApp',
  });

  const {
    mutateAsync: postCancelAccountAccess,
    isError: isCancelError,
    error: cancelError,
    isPending: isCancelFetching,
  } = usePostGlobalApiAccountAccessCancel({
    accountAccessId,
    authToken,
    voltApp: 'scaApp',
  });

  const cancelAccountAccess = useCallback(async () => {
    return postCancelAccountAccess()
      .then((result) => ({ result, error: null }))
      .catch((error) => ({ result: null, error }));
  }, [postCancelAccountAccess]);

  const updateAccountAccess = useCallback(
    async (body: PatchScaSubjectBody) => {
      if ('openBankingEU' in body) {
        return Promise.reject(
          'openBankingEU is not allowed to be updated in this context.'
        );
      }

      return patchAccountAccess(body)
        .then((result) => ({ result, error: null }))
        .catch((error) => ({ result: null, error }));
    },
    [patchAccountAccess]
  );

  const refetchAccountAccess = () =>
    retryGet({ throwOnError: true }).then((it) => it.data);

  const error = getError || patchError;
  const isLoading = isGetLoading;
  const isFetching = isGetLoading || isPatchFetching;
  const isError = isGetError || isPatchError;
  const isLoadingError = isGetError;
  const loadingError = getError;

  return (
    <ScaAccountAccessContext.Provider
      value={{
        accountAccess,
        updateAccountAccess,
        refetchAccountAccess,
        error,
        isLoading,
        isLoadingError,
        loadingError,
        isFetching,
        isError,
        cancelAccountAccess,
        isCancelFetching,
        isCancelError,
        cancelError,
      }}
    >
      {children}
    </ScaAccountAccessContext.Provider>
  );
};

export const withScaAccountAccessContextProvider =
  (props: Omit<ScaAccountAccessContextProviderProps, 'children'>) =>
  ({ children }: PropsWithChildren) => (
    <ScaAccountAccessContextProvider {...props}>
      {children}
    </ScaAccountAccessContextProvider>
  );

export const withScaAccountAccessContextDecorator = (
  data?: Partial<ScaAccountAccessContextData>
) => ({
  context: ScaAccountAccessContext as Context<ScaAccountAccessContextData>,
  contextValue: ({ args }: { args: Partial<ScaAccountAccessContextData> }) => ({
    ...SCA_ACCOUNT_ACCESS_CONTEXT_DEFAULT,
    ...data,
    ...args,
  }),
});
